﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, Select, TextField,
  EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, Badge, Thumbnail,
} from '@shopify/polaris';
import { StarFilledIcon, CheckCircleIcon } from '@shopify/polaris-icons';
import { useReviews } from '../../hooks/useReviews';
import type { ProductReview, ReviewStatus } from '../../api/reviews';
import { IconBadge } from '../shared/IconBadge';

const STATUS_OPTIONS: { label: string; value: string }[] = [
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

function Stars({ rating }: { rating: number }) {
  return (
    <Text as="span" tone="caution">
      {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
    </Text>
  );
}

function statusBadgeTone(status: ReviewStatus): 'attention' | 'success' | 'critical' {
  if (status === 'approved') return 'success';
  if (status === 'rejected') return 'critical';
  return 'attention';
}

interface ReplyBoxProps {
  review: ProductReview;
  onReply: (id: number, text: string) => Promise<ProductReview | null>;
}

function ReplyBox({ review, onReply }: ReplyBoxProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(review.merchant_reply ?? '');
  const [saving, setSaving] = useState(false);

  if (review.merchant_reply && !editing) {
    return (
      <Box background="bg-surface" borderRadius="200" padding="300">
        <BlockStack gap="100">
          <Text as="p" variant="bodySm" fontWeight="semibold">Your reply</Text>
          <Text as="p" variant="bodySm">{review.merchant_reply}</Text>
          <Box>
            <Button variant="plain" size="slim" onClick={() => setEditing(true)}>Edit reply</Button>
          </Box>
        </BlockStack>
      </Box>
    );
  }

  if (!editing) {
    return (
      <Box>
        <Button variant="plain" size="slim" onClick={() => setEditing(true)}>Reply publicly</Button>
      </Box>
    );
  }

  return (
    <BlockStack gap="150">
      <TextField
        label="Reply"
        labelHidden
        multiline={2}
        autoComplete="off"
        placeholder="Thank the customer or address their feedback — your reply is shown publicly under the review."
        value={text}
        onChange={setText}
      />
      <InlineStack gap="150">
        <Button
          size="slim"
          variant="primary"
          loading={saving}
          disabled={!text.trim()}
          onClick={async () => {
            setSaving(true);
            const result = await onReply(review.id, text.trim());
            setSaving(false);
            if (result) setEditing(false);
          }}
        >
          Post reply
        </Button>
        <Button size="slim" variant="plain" onClick={() => setEditing(false)}>Cancel</Button>
      </InlineStack>
    </BlockStack>
  );
}

export function ReviewsPanel() {
  const {
    reviews, loading, error, total, page, lastPage, statusFilter, setStatusFilter,
    fetchReviews, moderate, reply, deleteReview, setError,
  } = useReviews();

  useEffect(() => { fetchReviews(1, statusFilter); }, []);

  const handleStatusChange = (value: string) => {
    const next = value as ReviewStatus;
    setStatusFilter(next);
    fetchReviews(1, next);
  };

  return (
    <Page fullWidth
      title="Product Reviews & UGC"
      subtitle="Moderate customer reviews submitted from your storefront before they go live"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">Reviews ({total})</Text>
                <Box minWidth="180px">
                  <Select label="" labelHidden options={STATUS_OPTIONS} value={statusFilter} onChange={handleStatusChange} />
                </Box>
              </InlineStack>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : reviews.length === 0 ? (
                <EmptyState
                  heading={`No ${statusFilter} reviews`}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    {statusFilter === 'pending'
                      ? "New reviews submitted from your storefront's Product Reviews widget will show up here for approval."
                      : `There are no ${statusFilter} reviews right now.`}
                  </p>
                </EmptyState>
              ) : (
                <BlockStack gap="300">
                  {reviews.map((r) => (
                    <Card key={r.id} background="bg-surface-secondary">
                      <BlockStack gap="200">
                        <InlineStack align="space-between" blockAlign="start" wrap={false} gap="200">
                          <InlineStack gap="300" blockAlign="center" wrap={false}>
                            <IconBadge icon={StarFilledIcon} color="orange" />
                            <BlockStack gap="050">
                              <Stars rating={r.rating} />
                              <InlineStack gap="150">
                                <Badge tone={statusBadgeTone(r.status)}>{r.status}</Badge>
                                {r.is_verified_purchase && (
                                  <Badge tone="success" icon={CheckCircleIcon}>Verified purchase</Badge>
                                )}
                              </InlineStack>
                            </BlockStack>
                          </InlineStack>
                          <Text as="span" variant="bodySm" tone="subdued">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                          </Text>
                        </InlineStack>

                        {r.product_title && (
                          <Text as="p" variant="bodySm" fontWeight="semibold">{r.product_title}</Text>
                        )}
                        {r.title && <Text as="p" fontWeight="semibold">{r.title}</Text>}
                        <Text as="p" variant="bodyMd">{r.body}</Text>

                        {r.photos.length > 0 && (
                          <InlineStack gap="150">
                            {r.photos.map((url, i) => (
                              <Thumbnail key={i} source={url} alt={`Review photo ${i + 1}`} size="small" />
                            ))}
                          </InlineStack>
                        )}

                        <Text as="p" variant="bodySm" tone="subdued">
                          {r.reviewer_name} &middot; {r.reviewer_email}
                        </Text>

                        {r.status === 'pending' && (
                          <InlineStack gap="200">
                            <Button variant="primary" size="slim" onClick={() => moderate(r.id, 'approved')}>Approve</Button>
                            <Button tone="critical" size="slim" onClick={() => moderate(r.id, 'rejected')}>Reject</Button>
                          </InlineStack>
                        )}
                        {r.status !== 'pending' && (
                          <InlineStack gap="200">
                            <Button variant="plain" tone="critical" size="slim" onClick={() => deleteReview(r.id)}>Delete</Button>
                          </InlineStack>
                        )}

                        {r.status === 'approved' && <ReplyBox review={r} onReply={reply} />}
                      </BlockStack>
                    </Card>
                  ))}
                </BlockStack>
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchReviews(page - 1, statusFilter)}
                    hasNext={page < lastPage}
                    onNext={() => fetchReviews(page + 1, statusFilter)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="200">
              <Text as="h2" variant="headingMd">One more step: add it to your product pages</Text>
              <Text as="p" tone="subdued">
                Go to <strong>Online Store &rarr; Themes &rarr; Customize</strong>, open a product page, and use{' '}
                <strong>Add block &rarr; Apps &rarr; Product Reviews</strong> to place the review list and submission
                form. This is a one-time setup per theme &mdash; new reviews you approve here appear automatically.
              </Text>
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
